// Active Strategy badge — header-mounted indicator showing which strategy is
// loaded right now. Reads from the global StrategyContext so the badge is
// always in sync with the canvas, regardless of which tab the user is on.
//
// Renders three pieces:
//   1. Source icon (template / AI / manual / saved / imported)
//   2. Short strategy name
//   3. Tiny cloud-sync dot (synced / syncing / offline)
// Click → small menu with "Clear strategy" + sync status text.

import React, { useState, useRef, useEffect } from 'react';
import { Book, Sparkles, Pencil, Save, Upload, FileQuestion, Cloud, CloudOff, Loader2, X } from 'lucide-react';
import { useStrategy } from '../../contexts/StrategyContext';

const ActiveStrategyBadge: React.FC = () => {
  const ctx = useStrategy();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [menuOpen]);

  if (!ctx.hasStrategy) {
    // Subtle "no strategy" state — keeps header height stable across loads.
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 text-slate-400 text-xs">
        <FileQuestion className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">No strategy loaded</span>
      </div>
    );
  }

  const SourceIcon = sourceIconFor(ctx.strategySource);
  const sourceLabel = sourceLabelFor(ctx.strategySource);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setMenuOpen(o => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-400/30 text-emerald-100 text-xs transition-colors max-w-[280px]"
        title={`Active strategy — ${sourceLabel}. Click for options.`}
      >
        <SourceIcon className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="font-semibold truncate">
          {ctx.strategyName ?? 'Unnamed strategy'}
        </span>
        {/* "was Fitness: X" chip — the strategy's name USED to include a
            "Fitness: X" fragment from a prior optimization. We stripped it
            from the displayed name (because it's a stale historical claim
            that misleads when read alongside live backtests) but preserved
            the value here as informational. Title attribute explains the
            staleness on hover. */}
        {typeof (ctx.meta?.staleFitnessNote) === 'number' && (
          <span
            className="text-amber-200/80 hidden md:inline flex-shrink-0 text-[10px] font-medium"
            title={`This strategy was previously optimized to fitness ${(ctx.meta?.staleFitnessNote as number).toFixed(2)}. The number is HISTORICAL — re-run the backtest or optimizer to see fresh metrics.`}
          >
            · was Fitness: {(ctx.meta?.staleFitnessNote as number).toFixed(2)}
          </span>
        )}
        <span className="text-emerald-300/70 hidden md:inline flex-shrink-0">
          · {ctx.blocks.length} blocks
        </span>
        <CloudStatusDot status={ctx.cloudSync.status} />
      </button>

      {menuOpen && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-white text-gray-800 rounded-md shadow-xl border border-gray-200 z-50 text-sm overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Active Strategy</div>
            <div className="font-semibold text-gray-900 truncate">{ctx.strategyName ?? 'Unnamed strategy'}</div>
            <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1">
              <SourceIcon className="w-3 h-3" /> {sourceLabel} · {ctx.blocks.length} blocks
            </div>
          </div>
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Cloud Sync</div>
            <div className="text-[11px] text-gray-700 mt-0.5 flex items-center gap-1.5">
              <CloudStatusDot status={ctx.cloudSync.status} solid />
              <span>{cloudSyncLabel(ctx.cloudSync.status)}</span>
              {ctx.cloudSync.lastSyncedAt && (
                <span className="text-gray-400 ml-auto text-[10px]">
                  {relativeAge(ctx.cloudSync.lastSyncedAt)}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => { ctx.clearStrategy(); setMenuOpen(false); }}
            className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center gap-1.5"
          >
            <X className="w-3 h-3" /> Clear active strategy
          </button>
        </div>
      )}
    </div>
  );
};

// ─── helpers ────────────────────────────────────────────────────────────────

function sourceIconFor(source: ReturnType<typeof useStrategy>['strategySource']) {
  switch (source) {
    case 'template': return Book;
    case 'ai':       return Sparkles;
    case 'manual':   return Pencil;
    case 'saved':    return Save;
    case 'imported': return Upload;
    default:         return FileQuestion;
  }
}

function sourceLabelFor(source: ReturnType<typeof useStrategy>['strategySource']) {
  switch (source) {
    case 'template': return 'Loaded from template';
    case 'ai':       return 'Generated by AI';
    case 'manual':   return 'Manually authored';
    case 'saved':    return 'Restored from history';
    case 'imported': return 'Imported';
    default:         return 'Loaded';
  }
}

function cloudSyncLabel(status: ReturnType<typeof useStrategy>['cloudSync']['status']): string {
  switch (status) {
    case 'synced':          return 'Synced to your account';
    case 'syncing':         return 'Syncing…';
    case 'offline':         return 'Offline — local changes only';
    case 'unauthenticated': return 'Not signed in';
    case 'idle':
    default:                return 'Idle';
  }
}

function relativeAge(ts: number): string {
  const d = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (d < 5) return 'just now';
  if (d < 60) return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

const CloudStatusDot: React.FC<{
  status: ReturnType<typeof useStrategy>['cloudSync']['status'];
  solid?: boolean;
}> = ({ status, solid }) => {
  if (status === 'syncing') {
    return <Loader2 className="w-3 h-3 animate-spin text-emerald-300" />;
  }
  if (status === 'synced') {
    return solid
      ? <Cloud className="w-3 h-3 text-emerald-500" />
      : <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 ml-1" />;
  }
  if (status === 'offline') {
    return solid
      ? <CloudOff className="w-3 h-3 text-orange-500" />
      : <span className="w-1.5 h-1.5 rounded-full bg-orange-400 ml-1" title="Offline" />;
  }
  if (status === 'unauthenticated') {
    return solid
      ? <CloudOff className="w-3 h-3 text-gray-400" />
      : <span className="w-1.5 h-1.5 rounded-full bg-gray-400 ml-1" title="Not signed in" />;
  }
  return null;
};

export default ActiveStrategyBadge;
